import React from 'react';
import { MdOutlineExpandLess, MdOutlineExpandMore } from 'react-icons/md';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { EmptyData } from '@src/assets/icons';
import CountryBadge from '@src/components/CountryBadge';
import WebinarActionCell from '@src/pages/WebinarAttendees/Components/WebinarActionCell';
import OCheck from '@src/shared/OCheck';
import OEmptyStatus from '@src/shared/OStatus';
import { useTable } from '@src/shared/OTable/useTable';
import { capitalize } from '@src/utils';
import { IAttendee } from '@src/webinar/types';
import { useAccordionSelection } from './useAccordionSelection';

import styles from './attendeesAccordion.module.scss';

interface Props {
  data: IAttendee[];
  setSelectedAttendees: (selected: IAttendee[]) => void;
  hasLoadMore?: boolean;
  loadMore?: () => void;
  tableRef?: React.MutableRefObject<any>;
  isEmptyFilters: boolean;
}

const AttendeesAccordion = ({ data, setSelectedAttendees, hasLoadMore, loadMore, tableRef, isEmptyFilters }: Props) => {
  const { observerRef } = useTable({
    data,
    columns: [],
    hasLoadMore,
    loadMore,
  });

  const { selectedRows, handleToggle, expanded, handleAccordionToggle } = useAccordionSelection(
    data,
    setSelectedAttendees,
    tableRef
  );

  return (
    <>
      {!data.length ? (
        <OEmptyStatus
          image={<EmptyData />}
          title={isEmptyFilters ? 'No attendees have registered the webinar yet' : 'No matching results found.'}
        />
      ) : (
        data.map((attendee, index) => {
          const isSelected = selectedRows.includes(attendee._id);
          const isLast = index === data.length - 1;
          const isExpanded = expanded === attendee._id;

          const Actions = (
            <WebinarActionCell
              attendee={{
                ...attendee,
                status: attendee.status ?? 'PENDING',
              }}
            />
          );

          return (
            <Accordion
              key={attendee._id}
              ref={isLast ? observerRef : null}
              className={styles.accordion}
              onChange={handleAccordionToggle(attendee._id)}
              expanded={isExpanded}
              onClick={(e) => e.stopPropagation()}
            >
              <AccordionSummary
                className={styles.accordionwrapper}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    gap: '27px',
                    display: 'grid',
                    gridTemplateColumns: isExpanded ? 'minmax(0, 1f)' : 'minmax(0, 1fr) 140px',
                    alignItems: 'center',
                  },
                }}
              >
                <div className={styles.rowContent}>
                  <OCheck
                    checked={isSelected}
                    onChange={() => handleToggle(attendee)}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.checkbox}
                  />

                  <div className={styles.nameEmail}>
                    <h3 className={styles.name}>{capitalize(attendee.firstName.replace('_', '-'))}</h3>
                    <p className={styles.email}>{attendee.email}</p>
                  </div>

                  {isExpanded ? <MdOutlineExpandLess size={20} /> : <MdOutlineExpandMore size={20} />}
                </div>

                {!isExpanded && (
                  <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                    {Actions}
                  </div>
                )}
              </AccordionSummary>

              <AccordionDetails className={styles.details}>
                <div className={styles.detailsContent}>
                  <Box className={styles.detailRow}>
                    <CountryBadge
                      countryCode={attendee.countryCode?.toLowerCase() || ''}
                      countryName={attendee?.city ?? undefined}
                    />
                  </Box>

                  <Box className={styles.detailRow}>
                    {attendee.company
                      ? attendee.company.toLowerCase().replace(/^./, (char) => char.toUpperCase())
                      : '-'}
                  </Box>
                </div>

                <div className={styles.statusAction}>
                  <span className={styles.typeUser}>
                    {attendee.type
                      ?.replace('_', '-')
                      .toLowerCase()
                      .replace(/^./, (char) => char.toUpperCase())}
                  </span>
                  {Actions}
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </>
  );
};

export default AttendeesAccordion;
